import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Delete,
  Visibility,
  CloudDownload,
  Add,
  AttachFile,
  Description,
} from "@material-ui/icons";
import { useDropzone } from "react-dropzone";
import Button from "components/CustomButtons/Button.js";
import NotificationContext from "views/Components/Context.js";
import PDFViewer from "views/Components/PDFViewer";
import {
  listInputs,
  listArtifacts,
  uploadPipelineFiles,
  deleteInputFiles,
  fetchMarkdownFromS3,
  fetchTexFromS3,
  deleteArtifact,
} from "views/APIs/NewProjectArtifacts";
import MDEditor from "@uiw/react-md-editor";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    marginBottom: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  dropzone: {
    minHeight: "100px",
    backgroundColor: "#f0f0f0",
    borderColor: "#20677b",
    border: "2px dashed",
    borderRadius: "5px",
    padding: "20px",
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    transition: "border-color 0.3s, background-color 0.3s",
    "&:hover": {
      borderColor: "#1a5566",
      backgroundColor: "#e8f4f8",
    },
  },
  dropzoneActive: {
    borderColor: "#4caf50",
    backgroundColor: "#e8f5e9",
  },
  tableContainer: {
    maxHeight: "400px",
    marginTop: theme.spacing(2),
  },
  chip: {
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  fileTypeChip: {
    fontSize: "0.7rem",
    height: "20px",
  },
  actionsCell: {
    whiteSpace: "nowrap",
  },
  selectorDialog: {
    minWidth: 500,
  },
  selectedCount: {
    marginLeft: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  emptyMessage: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

// Helper to get file type badge color
const getFileTypeBadge = (format) => {
  const typeColors = {
    pdf: { bg: "#ffebee", color: "#c62828" },
    tex: { bg: "#e8f5e9", color: "#2e7d32" },
    md: { bg: "#e3f2fd", color: "#1565c0" },
    markdown: { bg: "#e3f2fd", color: "#1565c0" },
    default: { bg: "#f5f5f5", color: "#616161" },
  };
  return typeColors[format?.toLowerCase()] || typeColors.default;
};

function getDateFromObjectId(objectId) {
  // The first 8 characters of the hex string represent the timestamp (4 bytes)
  const timestampHex = objectId.substring(0, 8);

  // Convert hex to an integer (seconds)
  const timestampSeconds = parseInt(timestampHex, 16);

  // Convert seconds to milliseconds for the JavaScript Date object
  return new Date(timestampSeconds * 1000);
}

export default function FileUploaderComponent({
  projectId,
  stage,
  onFilesChange,
  selectedFileIds = [],
  onSelectionChange,
  disabled = false,
  showUploader = true,
  autoIncludeStages = null,
}) {
  const classes = useStyles();
  const { showNotification } = React.useContext(NotificationContext);

  // State
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [inputFiles, setInputFiles] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(selectedFileIds);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [fileContent, setFileContent] = useState("");

  // Combined files list (inputs + artifacts)
  const allFiles = React.useMemo(() => {
    const inputItems = inputFiles.map((f) => ({
      id: f.input_file_id,
      name: f.filename,
      type: "input",
      kind: f.kind,
      format: f.filename?.split(".").pop() || "file",
      url: f.s3?.public_url,
      s3: f.s3,
      pipelineStage: f.pipeline_stage,
    }));

    const artifactItems = artifacts.map((a) => ({
      id: a.artifact_id,
      name: `${a.type}.${a.format}`,
      type: "artifact",
      kind: a.type,
      format: a.format,
      url: a.s3?.public_url,
      s3: a.s3,
      pipelineStage: a.pipeline_stage,
    }));

    return [...inputItems, ...artifactItems];
  }, [inputFiles, artifacts]);

  // Fetch files and artifacts
  const fetchFiles = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const [inputsData, artifactsData] = await Promise.all([
        listInputs(projectId),
        listArtifacts(projectId),
      ]);

      // Filter out instructions from inputs
      const files = (inputsData.files || []).filter((f) => f.kind === "file");
      setInputFiles(files);
      setArtifacts(artifactsData || []);

      if (onFilesChange) {
        onFilesChange([...files, ...artifactsData]);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
      showNotification("Error loading files", "danger");
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (!projectId || acceptedFiles.length === 0) return;

      setUploading(true);
      try {
        await uploadPipelineFiles(projectId, acceptedFiles);
        showNotification(
          `${acceptedFiles.length} file(s) uploaded successfully`,
          "success",
        );
        await fetchFiles();
      } catch (error) {
        console.error("Error uploading files:", error);
        showNotification("Error uploading files", "danger");
      } finally {
        setUploading(false);
      }
    },
    [projectId],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: disabled || uploading,
    multiple: true,
  });

  // Handle file selection toggle
  const handleToggleSelection = (fileId) => {
    const newSelection = selectedIds.includes(fileId)
      ? selectedIds.filter((id) => id !== fileId)
      : [...selectedIds, fileId];

    setSelectedIds(newSelection);
    if (onSelectionChange) {
      onSelectionChange(newSelection);
    }
  };

  // Handle delete file
  const handleDeleteFile = async (file) => {
    try {
      if (file.type === "input") {
        await deleteInputFiles(projectId, [file.id]);
      } else if (file.type === "artifact") {
        await deleteArtifact(projectId, file.id);
      } else {
        return;
      }

      showNotification(
        `${file.type === "artifact" ? "Artifact" : "File"} deleted successfully`,
        "success",
      );
      await fetchFiles();

      // Remove from selection if selected
      if (selectedIds.includes(file.id)) {
        const newSelection = selectedIds.filter((id) => id !== file.id);
        setSelectedIds(newSelection);
        if (onSelectionChange) {
          onSelectionChange(newSelection);
        }
      }
    } catch (error) {
      console.error("Error deleting:", error);
      showNotification("Error deleting file", "danger");
    }
  };

  // Handle preview
  const handlePreview = async (file) => {
    const format = file.format?.toLowerCase();
    if (format === "pdf") {
      setPreviewFile(file);
      setPreviewOpen(true);
    } else if (format === "md" || format === "markdown" || format === "tex") {
      setPreviewFile(file);
      setPreviewOpen(true);
      setPreviewLoading(true);
      setFileContent("");
      try {
        let data;
        if (format === "tex") {
          data = await fetchTexFromS3(projectId, file.url);
        } else {
          data = await fetchMarkdownFromS3(projectId, file.url);
        }
        setFileContent(data?.tex || data?.markdown || "");
      } catch (error) {
        console.error("Error fetching preview content:", error);
        showNotification("Error loading preview content", "danger");
      } finally {
        setPreviewLoading(false);
      }
    } else if (file.url) {
      window.open(file.url, "_blank");
    }
  };

  // Handle download
  const handleDownload = (file) => {
    if (file.url) {
      const link = document.createElement("a");
      link.href = file.url;
      link.download = file.name;
      link.click();
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchFiles();
  }, [projectId]);

  // Sync selected IDs from props
  useEffect(() => {
    setSelectedIds(selectedFileIds);
  }, [selectedFileIds]);

  // Selected files list
  const selectedFiles = allFiles.filter((f) => selectedIds.includes(f.id));

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={24} />
        <Typography variant="body2" style={{ marginLeft: 8 }}>
          Loading files...
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: 600 }}>
          Files & Artifacts
        </Typography>
      </Box>

      {/* Dropzone for new uploads */}
      {showUploader && (
        <Box
          {...getRootProps()}
          className={`${classes.dropzone} ${
            isDragActive ? classes.dropzoneActive : ""
          }`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <Box display="flex" alignItems="center">
              <CircularProgress size={20} style={{ marginRight: 8 }} />
              <Typography>Uploading...</Typography>
            </Box>
          ) : isDragActive ? (
            <Typography>Drop files here...</Typography>
          ) : (
            <Box textAlign="center">
              <Add style={{ fontSize: 32, color: "#20677b" }} />
              <Typography>
                Drag & drop files here, and then select from the table below to
                include in the pipeline.
              </Typography>
            </Box>
          )}
        </Box>
      )}

                <Divider style={{ marginTop: "20px", marginBottom: "20px" }} />

      {/* Selected files chips */}
      {selectedFiles.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Selected for Pipeline:
          </Typography>
          <Box display="flex" flexWrap="wrap">
            {selectedFiles.map((file) => (
              <Chip
                key={file.id}
                label={file.name}
                icon={
                  file.type === "artifact" ? <Description /> : <AttachFile />
                }
                onDelete={() => handleToggleSelection(file.id)}
                className={classes.chip}
                size="small"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      {console.log("All files:", allFiles)}

      {/* Files table */}
      {allFiles.length > 0 ? (
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">Select</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Format</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Date</TableCell>
                <TableCell className={classes.actionsCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allFiles.map((file) => {
                const badge = getFileTypeBadge(file.format);
                const defaultAutoIncludeStages = stage === 0 ? [] : [stage - 1];
                const includedStages = autoIncludeStages ?? defaultAutoIncludeStages;
                const isAutoIncluded = includedStages.includes(file.pipelineStage);
                const isDisabled = disabled || file.format === "tex" || isAutoIncluded;
                const disabledReason = file.format === "tex"
                  ? "TeX files cannot be selected for pipeline"
                  : isAutoIncluded
                  ? "This file is already included for the next stage."
                  : "";
                
                return (
                  <TableRow key={file.id} hover>
                    <TableCell padding="checkbox">
                      <Tooltip title={disabledReason} arrow placement="top">
                        <span>
                          <Checkbox
                            checked={selectedIds.includes(file.id) || (isAutoIncluded && file.format !== "tex")}
                            onChange={() => handleToggleSelection(file.id)}
                            disabled={isDisabled}
                            size="small"
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={file.name}>
                        <Typography
                          variant="body2"
                          style={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.name}
                        </Typography>
                      </Tooltip>
                    </TableCell>


                    <TableCell>
                      <Chip
                        label={file.format?.toUpperCase()}
                        size="small"
                        className={classes.fileTypeChip}
                        style={{
                          backgroundColor: badge.bg,
                          color: badge.color,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          file.type === "artifact" ? "Generated" : "Uploaded"
                        }
                        size="small"
                        className={classes.fileTypeChip}
                        color={file.type === "artifact" ? "primary" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {getDateFromObjectId(file.id).toLocaleString()}
                    </TableCell>
                    <TableCell className={classes.actionsCell}>
                      <Tooltip title="Preview">
                        <IconButton
                          size="small"
                          onClick={() => handlePreview(file)}
                          disabled={!file.url}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          size="small"
                          onClick={() => handleDownload(file)}
                          disabled={!file.url}
                        >
                          <CloudDownload fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteFile(file)}
                          disabled={disabled}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box className={classes.emptyMessage}>
          <Typography variant="body2">
            No files uploaded yet. Use the dropzone above to upload files or
            click "Select Existing" to attach generated artifacts.
          </Typography>
        </Box>
      )}

      

      {/* File selector dialog */}
      <Dialog
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Files & Artifacts</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            Select files or previously generated artifacts to include in the
            pipeline:
          </Typography>

          <TableContainer style={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Select</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allFiles.map((file) => (
                  <TableRow
                    key={file.id}
                    hover
                    onClick={() => handleToggleSelection(file.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(file.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{file.name}</TableCell>
                    <TableCell>{file.kind || file.type}</TableCell>
                    <TableCell>
                      {file.type === "artifact" ? "Generated" : "Uploaded"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectorOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">{previewFile?.name}</Typography>
            <Button
              onClick={() => setPreviewOpen(false)}
              color="transparent"
              size="sm"
            >
              Close
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers style={{ padding: 0 }}>
          {previewFile?.format?.toLowerCase() === "pdf" ? (
            <PDFViewer
              pdfUrl={previewFile.url}
              scrollView={true}
              showButtons={false}
            />
          ) : previewLoading ? (
            <Box
              p={4}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <CircularProgress />
              <Typography variant="body2" style={{ marginLeft: 16 }}>
                Loading preview...
              </Typography>
            </Box>
          ) : (
            <div data-color-mode="light">
              <MDEditor.Markdown
                source={fileContent}
                style={{ padding: "20px", minHeight: "400px" }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
